import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Table, Alert, Modal, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, 
  faFileContract,
  faUser,
  faSignOutAlt,
  faKey,
  faEye,
  faEyeSlash,
  faCalendarAlt,
  faMoneyBillWave,
  faWrench
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { contractService } from '../../services/contractService';
import { userService } from '../../services/userService';
import { tenantService } from '../../services/tenantService';
import { maintenanceService } from '../../services/maintenanceService';

const TenantDashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const [contracts, setContracts] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Password change modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      console.log('Fetching dashboard data for current user:', currentUser?.uid);
      
      // Get user profile
      const profile = await userService.getUserProfile();
      console.log('User profile found:', profile);
      setUserProfile(profile);
      
      // Get tenant document using the authUid or email
      console.log('Fetching tenants for company:', profile.companyId);
      const tenants = await tenantService.getTenantsByCompany(profile.companyId);
      console.log('All tenants for company:', tenants);
      
      let tenant = tenants.find(t => t.authUid === profile.uid);
      
      // If not found by authUid, try to find by email
      if (!tenant) {
        console.log('Tenant not found by authUid, looking by email:', profile.email);
        tenant = tenants.find(t => t.email === profile.email);
      }
      
      console.log('Looking for tenant with authUid:', profile.uid);
      console.log('Found tenant:', tenant);
      
      if (!tenant) {
        console.error('Tenant document not found for user:', profile.uid);
        console.error('Available tenants:', tenants.map(t => ({ id: t.id, authUid: t.authUid, email: t.email })));
        setError('Tenant profile not found. Please contact your property manager.');
        return;
      }
      
      console.log('Found tenant document:', tenant);
      
      // Get contracts for this tenant using the tenant document ID
      const contractsData = await contractService.getContractsByTenant(tenant.id);
      setContracts(contractsData);
      
      // Get maintenance requests for this tenant
      const maintenanceData = await maintenanceService.getMaintenanceRequestsByTenant(tenant.id);
      setMaintenanceRequests(maintenanceData);
      
      console.log('Found contracts for tenant:', contractsData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setChangingPassword(true);
      setError('');
      
      await userService.updatePassword(passwordForm.currentPassword, passwordForm.newPassword);
      
      setShowPasswordModal(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Show success message
      alert('Password updated successfully!');
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Failed to change password: ' + (error.message || error));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const symbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      AED: 'د.إ',
      SAR: 'ر.س',
      PKR: '₨',
      INR: '₹'
    };
    return `${symbols[currency] || '$'}${parseFloat(amount || 0).toLocaleString()}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: 'success', text: 'Active' },
      expired: { variant: 'danger', text: 'Expired' },
      terminated: { variant: 'secondary', text: 'Terminated' },
      pending: { variant: 'warning', text: 'Pending' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Tenant Dashboard</h2>
          <p className="text-muted mb-0">Welcome back, {userProfile?.name || currentUser?.displayName}</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => setShowPasswordModal(true)}>
            <FontAwesomeIcon icon={faKey} className="me-2" />
            Change Password
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faFileContract} className="text-primary" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{contracts.length}</h4>
                  <p className="text-muted mb-0">Active Contracts</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faHome} className="text-success" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{contracts.filter(c => c.status === 'active').length}</h4>
                  <p className="text-muted mb-0">Current Rentals</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-info bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-info" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{contracts.filter(c => c.status === 'expired').length}</h4>
                  <p className="text-muted mb-0">Expired Contracts</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faMoneyBillWave} className="text-warning" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">
                    {formatCurrency(
                      contracts
                        .filter(c => c.status === 'active')
                        .reduce((sum, c) => sum + (c.monthlyRent || (c.totalRentalAmount && c.contractDuration ? c.totalRentalAmount / c.contractDuration : c.totalRentalAmount / 12) || 0), 0),
                      'USD'
                    )}
                  </h4>
                  <p className="text-muted mb-0">Monthly Rent</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Contracts */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faFileContract} className="text-primary me-2" />
            My Contracts
          </h5>
        </Card.Header>
        <Card.Body>
          {contracts.length === 0 ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faFileContract} className="text-muted mb-3" size="3x" />
              <h5 className="text-muted">No contracts found</h5>
              <p className="text-muted">You don't have any contracts yet.</p>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Contract ID</th>
                  <th>Property Unit</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Monthly Rent</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((contract) => (
                  <tr key={contract.id}>
                    <td>
                      <strong className="text-primary">{contract.contractId || contract.id}</strong>
                    </td>
                    <td>{contract.propertyUnitId || 'N/A'}</td>
                    <td>
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {contract.contractEndDate ? new Date(contract.contractEndDate).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                      {formatCurrency(
                        contract.monthlyRent || (contract.totalRentalAmount && contract.contractDuration ? contract.totalRentalAmount / contract.contractDuration : contract.totalRentalAmount / 12) || 0, 
                        contract.currency || 'USD'
                      )}
                    </td>
                    <td>{getStatusBadge(contract.status)}</td>
                    <td>
                      <Button 
                        as={Link} 
                        to={`/tenant/contracts/${contract.id}`} 
                        variant="outline-primary" 
                        size="sm"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Maintenance Requests */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-transparent border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faWrench} className="text-primary me-2" />
              Maintenance Requests
            </h5>
            <Button as={Link} to="/tenant/maintenance/new" variant="primary" size="sm">
              <FontAwesomeIcon icon={faWrench} className="me-2" />
              New Request
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {maintenanceRequests.length === 0 ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faWrench} className="text-muted mb-3" size="3x" />
              <h5 className="text-muted">No maintenance requests</h5>
              <p className="text-muted">You haven't submitted any maintenance requests yet.</p>
              <Button as={Link} to="/tenant/maintenance/new" variant="primary">
                <FontAwesomeIcon icon={faWrench} className="me-2" />
                Submit Your First Request
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {maintenanceRequests.slice(0, 5).map((request) => (
                  <tr key={request.id}>
                    <td>
                      <strong className="text-primary">{request.requestId}</strong>
                    </td>
                    <td>{request.title}</td>
                    <td>
                      <Badge bg="secondary">
                        {request.type === 'plumbing' ? 'Plumbing' :
                         request.type === 'electrical' ? 'Electrical' :
                         request.type === 'hvac' ? 'HVAC' :
                         request.type === 'appliance' ? 'Appliance' :
                         request.type === 'structural' ? 'Structural' :
                         request.type === 'pest_control' ? 'Pest Control' :
                         request.type === 'cleaning' ? 'Cleaning' :
                         request.type === 'landscaping' ? 'Landscaping' :
                         request.type === 'security' ? 'Security' :
                         request.type}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={
                        request.status === 'pending' ? 'warning' :
                        request.status === 'in_progress' ? 'info' :
                        request.status === 'completed' ? 'success' :
                        'secondary'
                      }>
                        {request.status === 'pending' ? 'Pending' :
                         request.status === 'in_progress' ? 'In Progress' :
                         request.status === 'completed' ? 'Completed' :
                         request.status === 'cancelled' ? 'Cancelled' :
                         request.status}
                      </Badge>
                    </td>
                    <td>
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {request.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </td>
                    <td>
                      <Button 
                        as={Link} 
                        to={`/tenant/maintenance/${request.id}`} 
                        variant="outline-primary" 
                        size="sm"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {maintenanceRequests.length > 5 && (
            <div className="text-center mt-3">
              <Button as={Link} to="/tenant/maintenance" variant="outline-secondary">
                View All Requests ({maintenanceRequests.length})
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Password Change Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faKey} className="me-2" />
            Change Password
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePasswordChange}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Current Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handleInputChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showNewPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handleInputChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirm New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={changingPassword}>
              {changingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default TenantDashboard; 